import { apiClient, attachMutatingRequestCsrfHeaders, extractDataFromResponse, extractArrayFromResponse, extractObjectFromResponse } from '../utils/apiClient';
import { emitNotificationsChanged } from '../utils/notificationEvents';
import { ApiResponse, PaginationParams } from '../../shared/types';
import { logger } from "../utils/logger";
// import { logger } from "../utils/logger";
// Temporary ApiError class
class ApiError extends Error {
    constructor(message: string, public status?: number) {
        super(message);
        this.name = 'ApiError';
    }
}
import { safeGetItem } from '../utils/storageUtils';
/**
 * Notification Service - Comprehensive notification management
 *
 * This service provides a complete interface for managing notifications, templates, and user preferences.
 * It includes advanced features like caching, error handling, validation, and real-time updates.
 *
 * Key Features:
 * - User notification management (CRUD operations)
 * - Admin notification management and broadcasting
 * - Notification template management
 * - User preference management
 * - Real-time notification subscriptions
 * - Intelligent caching with TTL and invalidation
 * - Comprehensive error handling and validation
 * - Request deduplication and performance optimization
 * - Strong TypeScript typing throughout
 * - Browser compatibility and security
 * - State management integration ready
 *
 * @class NotificationService
 * @version 2.0.0
 * @since 1.0.0
 *
 * @example
 * ```typescript
 * import notificationService from '../services/notificationService';
 *
 * // Get user notifications with caching
 * const notifications = await notificationService.getUserNotifications({ page: 1, limit: 20 });
 *
 * // Send notification to multiple users
 * const result = await notificationService.sendNotificationToUsers({
 *   userIds: [1, 2, 3],
 *   type: 'system_alert',
 *   title: 'System Maintenance',
 *   message: 'Scheduled maintenance will occur tonight'
 * });
 *
 * // Subscribe to real-time notifications
 * const unsubscribe = notificationService.subscribeToNotifications((notification) => {
 *   logger.debug('New notification:', notification);
 * });
 *
 * // Cache management
 * notificationService.clearCache();
 * const stats = notificationService.getCacheStats();
 * ```
 */
// ========================================
// NOTIFICATION INTERFACES
// ========================================
export interface Notification {
    id: number;
    user_id: number;
    type: string;
    title: string;
    message: string;
    data?: Record<string, unknown>;
    priority: 'low' | 'normal' | 'high';
    read: boolean;
    created_at: string;
    read_at?: string;
}
export interface NotificationTemplate {
    id: number;
    name: string;
    type: string;
    title: string;
    message: string;
    variables: string[];
    is_active: boolean;
    created_at: string;
    updated_at: string;
}
export interface UserNotificationPreferences {
    id: number;
    user_id: number;
    email_notifications: boolean;
    push_notifications: boolean;
    sms_notifications: boolean;
    notification_types: string[];
    quiet_hours_start?: string;
    quiet_hours_end?: string;
    created_at: string;
    updated_at: string;
}
export interface SendNotificationRequest {
    userIds: number[];
    type: string;
    title: string;
    message: string;
    data?: Record<string, unknown>;
    priority?: 'low' | 'normal' | 'high';
}
export interface SubmissionStatusUpdateRequest {
    submissionId: number;
    submissionType: 'event' | 'venue';
    status: string;
    message?: string;
    priority?: 'low' | 'normal' | 'high';
}
export interface ApprovalRequestNotification {
    submissionId: number;
    submissionType: 'event' | 'venue';
    reviewerId: number;
    message?: string;
    priority?: 'low' | 'normal' | 'high';
}
// ========================================
// SERVICE CONFIGURATION INTERFACES
// ========================================
export interface NotificationServiceConfig {
    baseUrl: string;
    defaultTimeout: number;
    defaultRetryAttempts: number;
    defaultRetryDelay: number;
    cacheEnabled: boolean;
    defaultCacheDuration: number;
    maxCacheSize: number;
}
export interface NotificationCacheEntry<T> {
    data: T;
    timestamp: number;
    expires: number;
}
export interface NotificationPendingRequest<T> {
    promise: Promise<T>;
    timestamp: number;
}
export interface NotificationServiceError {
    message: string;
    code: string;
    status?: number;
    timestamp: string;
    userAgent?: string;
}
export interface NotificationApiRequestOptions {
    timeout?: number;
    retryAttempts?: number;
    retryDelay?: number;
    cache?: boolean;
    cacheDuration?: number;
    requiresAuth?: boolean;
}
export interface NotificationFilters extends PaginationParams {
    userId?: number;
    type?: string;
    priority?: string;
    isRead?: boolean;
    dateRange?: string;
    search?: string;
}
export interface NotificationListResponse {
    notifications: Notification[];
    pagination: {
        page: number;
        limit: number;
        total: number;
        totalPages: number;
    };
}
export interface NotificationCountResponse {
    total: number;
    unread: number;
}
export interface UnifiedItem {
    id: string;
    sourceId: number;
    sourceType: 'notification' | 'message';
    title: string;
    message: string;
    type: string;
    priority: 'low' | 'normal' | 'high' | 'urgent';
    read: boolean;
    created_at: string;
    metadata: Record<string, any>;
}
export interface UnifiedNotificationResponse {
    items: UnifiedItem[];
    counts: {
        total: number;
        notifications: number;
        messages: number;
        unreadNotifications: number;
        unreadMessages: number;
        unreadTotal: number;
    };
}
class NotificationService {
    private readonly config: NotificationServiceConfig = {
        baseUrl: '/api',
        defaultTimeout: 30000, // 30 seconds
        defaultRetryAttempts: 3,
        defaultRetryDelay: 1000, // 1 second
        cacheEnabled: true,
        defaultCacheDuration: 300000, // 5 minutes
        maxCacheSize: 100
    };
    private readonly requestCache: Map<string, NotificationCacheEntry<any>> = new Map();
    private readonly pendingRequests: Map<string, NotificationPendingRequest<any>> = new Map();
    private readonly errorLog: NotificationServiceError[] = [];
    private readonly maxErrorLogSize = 50;
    // ========================================
    // CORE HTTP METHODS
    // ========================================
    /**
     * Perform GET request with caching and error handling
     *
     * @param endpoint - API endpoint (without /api prefix)
     * @param options - Request options including caching and retry configuration
     * @returns Promise resolving to response data
     */
    private async get<T = any>(endpoint: string, options: NotificationApiRequestOptions = {}): Promise<T> {
        return this.makeRequest<T>('GET', endpoint, undefined, options);
    }
    /**
     * Perform POST request with error handling and retry logic
     *
     * @param endpoint - API endpoint (without /api prefix)
     * @param data - Request body data
     * @param options - Request options including retry and timeout configuration
     * @returns Promise resolving to response data
     */
    private async post<T = any>(endpoint: string, data?: any, options: NotificationApiRequestOptions = {}): Promise<T> {
        return this.makeRequest<T>('POST', endpoint, data, options);
    }
    /**
     * Perform PUT request with error handling and retry logic
     *
     * @param endpoint - API endpoint (without /api prefix)
     * @param data - Request body data
     * @param options - Request options including retry and timeout configuration
     * @returns Promise resolving to response data
     */
    private async put<T = any>(endpoint: string, data?: any, options: NotificationApiRequestOptions = {}): Promise<T> {
        return this.makeRequest<T>('PUT', endpoint, data, options);
    }
    /**
     * Perform DELETE request with error handling and retry logic
     *
     * @param endpoint - API endpoint (without /api prefix)
     * @param options - Request options including retry and timeout configuration
     * @returns Promise resolving to response data
     */
    private async delete<T = any>(endpoint: string, options: NotificationApiRequestOptions = {}): Promise<T> {
        return this.makeRequest<T>('DELETE', endpoint, undefined, options);
    }
    /**
     * Core request method with caching, retry logic, and error handling
     */
    private async makeRequest<T>(method: string, endpoint: string, data?: any, options: NotificationApiRequestOptions = {}): Promise<T> {
        const { timeout = this.config.defaultTimeout, retryAttempts = this.config.defaultRetryAttempts, retryDelay = this.config.defaultRetryDelay, cache = this.config.cacheEnabled, cacheDuration = this.config.defaultCacheDuration, requiresAuth = true } = options;
        const cacheKey = this.generateCacheKey(method, endpoint, data);
        // Check cache for GET requests
        if (method === 'GET' && cache) {
            const cachedData = this.getCachedData<T>(cacheKey);
            if (cachedData !== null) {
                return cachedData;
            }
        }
        // Check for pending requests to prevent duplicates
        if (this.pendingRequests.has(cacheKey)) {
            return this.pendingRequests.get(cacheKey)!.promise;
        }
        const requestPromise = this.executeRequest<T>(method, endpoint, data, {
            timeout,
            retryAttempts,
            retryDelay,
            requiresAuth
        });
        // Store pending request
        this.pendingRequests.set(cacheKey, {
            promise: requestPromise,
            timestamp: Date.now()
        });
        try {
            const result = await requestPromise;
            // Cache successful GET requests
            if (method === 'GET' && cache && result) {
                this.setCachedData(cacheKey, result, cacheDuration);
            }
            return result;
        }
        finally {
            // Clean up pending request
            this.pendingRequests.delete(cacheKey);
        }
    }
    /**
     * Execute the actual HTTP request with retry logic
     */
    private async executeRequest<T>(method: string, endpoint: string, data: any, options: {
        timeout: number;
        retryAttempts: number;
        retryDelay: number;
        requiresAuth: boolean;
    }): Promise<T> {
        const { timeout, retryAttempts, retryDelay, requiresAuth } = options;
        let lastError: Error | null = null;
        for (let attempt = 1; attempt <= retryAttempts; attempt++) {
            try {
                const headers: Record<string, string> = {
                    'Content-Type': 'application/json',
                    ...(requiresAuth && this.getAuthHeaders())
                };
                await attachMutatingRequestCsrfHeaders(method, headers);
                const requestOptions: RequestInit = {
                    method,
                    headers,
                    ...(data && { body: JSON.stringify(data) })
                };
                const controller = new AbortController();
                const timeoutId = setTimeout(() => controller.abort(), timeout);
                const response = await fetch(`${this.config.baseUrl}${endpoint}`, {
                    ...requestOptions,
                    credentials: 'include', // Session-cookie auth (backoffice + main site)
                    signal: controller.signal
                });
                clearTimeout(timeoutId);
                if (!response.ok) {
                    throw new ApiError(`HTTP ${response.status}: ${response.statusText}`, response.status, 'HTTP_ERROR', { endpoint, method, status: response.status });
                }
                const responseData = await response.json();
                return extractDataFromResponse<T>(responseData, null as T);
            }
            catch (error) {
                lastError = error as Error;
                // Don't retry on certain errors
                if (error instanceof ApiError && (error.status === 401 || error.status === 403 || error.status === 404 || error.status === 503)) {
                    throw error;
                }
                // Log error
                this.logError(error as Error, endpoint, method);
                // Wait before retry (except on last attempt)
                if (attempt < retryAttempts) {
                    await new Promise(resolve => setTimeout(resolve, retryDelay * attempt));
                }
            }
        }
        throw lastError || new Error('Request failed after all retry attempts');
    }
    /**
     * Get authentication headers
     */
    private getAuthHeaders(): Record<string, string> {
        const token = safeGetItem('auth_token');
        return token ? { Authorization: `Bearer ${token}` } : {};
    }
    /**
     * Generate cache key for request
     */
    private generateCacheKey(method: string, endpoint: string, data?: any): string {
        const dataString = data ? JSON.stringify(data) : '';
        return `${method}:${endpoint}:${dataString}`;
    }
    /**
     * Get cached data if valid
     */
    private getCachedData<T>(key: string): T | null {
        const cached = this.requestCache.get(key);
        if (!cached)
            return null;
        if (Date.now() > cached.expires) {
            this.requestCache.delete(key);
            return null;
        }
        return cached.data as T;
    }
    /**
     * Set cached data with expiration
     */
    private setCachedData<T>(key: string, data: T, duration: number): void {
        // Clean up old cache entries if we're at the limit
        if (this.requestCache.size >= this.config.maxCacheSize) {
            this.cleanupCache();
        }
        this.requestCache.set(key, {
            data,
            timestamp: Date.now(),
            expires: Date.now() + duration
        });
    }
    /**
     * Clean up expired cache entries
     */
    private cleanupCache(): void {
        const now = Date.now();
        for (const [key, entry] of this.requestCache.entries()) {
            if (now > entry.expires) {
                this.requestCache.delete(key);
            }
        }
    }
    /**
     * Log error for debugging
     */
    private logError(error: Error, endpoint: string, method: string): void {
        const errorEntry: NotificationServiceError = {
            message: error.message,
            code: error.name,
            status: (error as Error & {
                status?: number;
            }).status,
            timestamp: new Date().toISOString(),
            userAgent: navigator.userAgent
        };
        this.errorLog.push(errorEntry);
        // Keep error log size manageable
        if (this.errorLog.length > this.maxErrorLogSize) {
            this.errorLog.shift();
        }
        logger.api(`NotificationService ${method} ${endpoint}:`, error);
    }
    // ========================================
    // USER NOTIFICATIONS
    // ========================================
    /**
     * Get user's notifications with caching and pagination
     *
     * @param filters - Filter and pagination options
     * @returns Promise resolving to notification list with pagination
     *
     * @example
     * ```typescript
     * // Get first page of notifications
     * const notifications = await notificationService.getUserNotifications({ page: 1, limit: 20 });
     *
     * // Get only unread notifications
     * const unread = await notificationService.getUserNotifications({
     *   page: 1,
     *   limit: 10,
     *   isRead: false
     * });
     * ```
     */
    async getUserNotifications(filters: NotificationFilters = {}): Promise<ApiResponse<NotificationListResponse>> {
        try {
            // Session-cookie auth is enough; do not require localStorage auth_token
            // (backoffice admins often have no bearer token — gating here caused empty lists with a non-zero badge).
            const { page = 1, limit = 20, isRead, type } = filters;
            const status = isRead === false ? 'unread' : isRead === true ? 'read' : 'all';
            const params = new URLSearchParams({
                page: page.toString(),
                limit: limit.toString(),
                status
            });
            if (type)
                params.append('type', type);
            const endpoint = `/notifications?${params.toString()}`;
            const response = await this.get<NotificationListResponse>(endpoint, {
                cache: true,
                cacheDuration: 60000 // 1 minute cache for notifications
            });
            return {
                success: true,
                data: response
            };
        }
        catch (error) {
            logger.error('Error fetching user notifications:', error, {});
            // Handle 401 errors more gracefully
            if (error instanceof ApiError && error.status === 401) {
                logger.debug('🔔 Authentication failed for notifications, user may need to log in again');
                return {
                    success: false,
                    error: 'Authentication expired. Please log in again.'
                };
            }
            return {
                success: false,
                error: error instanceof Error ? error.message : 'Failed to fetch notifications'
            };
        }
    }
    /**
     * Get unified notifications and messages
     * Fetches both system notifications and user messages in a single response
     *
     * @param limit - Maximum number of items to fetch (default: 10, max: 50)
     * @returns Promise resolving to unified notification response
     *
     * @example
     * ```typescript
     * const result = await notificationService.getUnifiedNotifications(10);
     * if (result.success) {
     *   logger.debug('Total unread:', result.data.counts.unreadTotal);
     *   result.data.items.forEach(item => {
     *     logger.debug(`${item.sourceType}: ${item.title}`);
     *   });
     * }
     * ```
     */
    async getUnifiedNotifications(limit: number = 10): Promise<ApiResponse<UnifiedNotificationResponse>> {
        try {
            // Rely on session cookies via credentials: 'include' — do not require auth_token
            const params = new URLSearchParams({
                limit: Math.min(limit, 50).toString()
            });
            const endpoint = `/notifications/unified?${params.toString()}`;
            const response = await this.get<UnifiedNotificationResponse>(endpoint, {
                cache: true,
                cacheDuration: 30000 // 30 second cache for unified notifications
            });
            return {
                success: true,
                data: response
            };
        }
        catch (error) {
            logger.error('Error fetching unified notifications:', error, {});
            if (error instanceof ApiError && error.status === 401) {
                logger.debug('🔔 Authentication failed for unified notifications');
                return {
                    success: false,
                    error: 'Authentication expired. Please log in again.'
                };
            }
            return {
                success: false,
                error: error instanceof Error ? error.message : 'Failed to fetch unified notifications'
            };
        }
    }
    /**
     * Get combined unread count for notifications and messages
     *
     * @returns Promise resolving to combined unread count
     *
     * @example
     * ```typescript
     * const count = await notificationService.getCombinedUnreadCount();
     * logger.debug(`You have ${count} unread items`);
     * ```
     */
    async getCombinedUnreadCount(): Promise<number> {
        try {
            const endpoint = '/notifications/unified?limit=1';
            const response = await this.get<UnifiedNotificationResponse>(endpoint, {
                cache: true,
                cacheDuration: 15000 // 15 second cache for unread count
            });
            return response.counts?.unreadTotal ?? 0;
        }
        catch (error) {
            logger.error('Error fetching combined unread count:', error, {});
            return 0;
        }
    }
    /**
     * Mark notification as read
     *
     * @param notificationId - ID of the notification to mark as read
     * @returns Promise resolving to operation result
     *
     * @example
     * ```typescript
     * const result = await notificationService.markAsRead(123);
     * if (result.success) {
     *   logger.debug('Notification marked as read');
     * }
     * ```
     */
    async markAsRead(notificationId: number): Promise<ApiResponse<void>> {
        try {
            await this.put(`/notifications/${notificationId}/read`, {}, {
                cache: false // Don't cache write operations
            });
            // Invalidate related cache entries
            this.invalidateNotificationCache();
            emitNotificationsChanged();
            return { success: true };
        }
        catch (error) {
            logger.error('Error marking notification as read:', error, {});
            return {
                success: false,
                error: error instanceof Error ? error.message : 'Failed to mark notification as read'
            };
        }
    }
    /**
     * Mark all notifications as read for the current user
     *
     * @returns Promise resolving to operation result
     *
     * @example
     * ```typescript
     * const result = await notificationService.markAllAsRead();
     * if (result.success) {
     *   logger.debug('All notifications marked as read');
     * }
     * ```
     */
    async markAllAsRead(): Promise<ApiResponse<void>> {
        try {
            await this.put('/notifications/read-all', {}, {
                cache: false // Don't cache write operations
            });
            // Invalidate related cache entries
            this.invalidateNotificationCache();
            emitNotificationsChanged();
            return { success: true };
        }
        catch (error) {
            logger.error('Error marking all notifications as read:', error, {});
            return {
                success: false,
                error: error instanceof Error ? error.message : 'Failed to mark all notifications as read'
            };
        }
    }
    /**
     * Delete a notification
     *
     * @param notificationId - ID of the notification to delete
     * @returns Promise resolving to operation result
     *
     * @example
     * ```typescript
     * const result = await notificationService.deleteNotification(123);
     * if (result.success) {
     *   logger.debug('Notification deleted');
     * }
     * ```
     */
    async deleteNotification(notificationId: number): Promise<ApiResponse<void>> {
        try {
            await this.delete(`/notifications/${notificationId}`, {
                cache: false // Don't cache write operations
            });
            // Invalidate related cache entries
            this.invalidateNotificationCache();
            return { success: true };
        }
        catch (error) {
            logger.error('Error deleting notification:', error, {});
            return {
                success: false,
                error: error instanceof Error ? error.message : 'Failed to delete notification'
            };
        }
    }
    /**
     * Get notification count for the current user
     *
     * @returns Promise resolving to notification count data
     *
     * @example
     * ```typescript
     * const count = await notificationService.getNotificationCount();
     * if (count.success && count.data) {
     *   logger.debug(`Total: ${count.data.total}, Unread: ${count.data.unread}`);
     * }
     * ```
     */
    async getNotificationCount(): Promise<ApiResponse<NotificationCountResponse>> {
        try {
            const response = await this.get<NotificationCountResponse>('/notifications/count', {
                cache: true,
                cacheDuration: 30000 // 30 second cache for count
            });
            return {
                success: true,
                data: response
            };
        }
        catch (error) {
            logger.error('Error fetching notification count:', error, {});
            return {
                success: false,
                error: error instanceof Error ? error.message : 'Failed to fetch notification count'
            };
        }
    }
    // ========================================
    // ADMIN NOTIFICATION MANAGEMENT
    // ========================================
    /**
     * Get all notifications (admin only)
     *
     * @param filters - Filter and pagination options
     * @returns Promise resolving to notification list with pagination
     *
     * @example
     * ```typescript
     * // Get all notifications for admin
     * const notifications = await notificationService.getAllNotifications({ page: 1, limit: 50 });
     *
     * // Filter by user and type
     * const filtered = await notificationService.getAllNotifications({
     *   page: 1,
     *   limit: 20,
     *   userId: 123,
     *   type: 'system_alert'
     * });
     * ```
     */
    async getAllNotifications(filters: NotificationFilters = {}): Promise<ApiResponse<NotificationListResponse>> {
        try {
            const { page = 1, limit = 50, userId, type, priority, isRead, search, dateRange, sortBy = 'created_at', sortOrder = 'desc' } = filters;
            const params = new URLSearchParams({
                page: page.toString(),
                limit: limit.toString(),
                sortBy,
                sortOrder
            });
            if (userId)
                params.append('userId', userId.toString());
            if (type)
                params.append('type', type);
            if (priority)
                params.append('priority', priority);
            if (isRead !== undefined)
                params.append('isRead', isRead.toString());
            if (search)
                params.append('search', search);
            if (dateRange)
                params.append('dateRange', dateRange);
            const endpoint = `/notifications/admin/all?${params.toString()}`;
            const response = await this.get<NotificationListResponse>(endpoint, {
                cache: true,
                cacheDuration: 120000 // 2 minute cache for admin data
            });
            return {
                success: true,
                data: response
            };
        }
        catch (error) {
            logger.error('Error fetching all notifications:', error, {});
            return {
                success: false,
                error: error instanceof Error ? error.message : 'Failed to fetch all notifications'
            };
        }
    }
    /**
     * Send notification to multiple users (admin only)
     *
     * @param request - Notification request data
     * @returns Promise resolving to operation result
     *
     * @example
     * ```typescript
     * const result = await notificationService.sendNotificationToUsers({
     *   userIds: [1, 2, 3],
     *   type: 'system_alert',
     *   title: 'System Maintenance',
     *   message: 'Scheduled maintenance will occur tonight',
     *   priority: 'high'
     * });
     * ```
     */
    async sendNotificationToUsers(request: SendNotificationRequest): Promise<ApiResponse<{
        sentCount: number;
        failedCount: number;
    }>> {
        try {
            const response = await this.post<{
                sentCount: number;
                failedCount: number;
            }>('/notifications/admin/send', request, {
                cache: false // Don't cache write operations
            });
            return {
                success: true,
                data: response
            };
        }
        catch (error) {
            logger.error('Error sending notification to users:', error, {});
            return {
                success: false,
                error: error instanceof Error ? error.message : 'Failed to send notification'
            };
        }
    }
    /**
     * Send submission status update notification
     *
     * @param request - Submission status update request
     * @returns Promise resolving to operation result
     *
     * @example
     * ```typescript
     * const result = await notificationService.sendSubmissionStatusUpdate({
     *   submissionId: 123,
     *   submissionType: 'event',
     *   status: 'approved',
     *   message: 'Your event submission has been approved'
     * });
     * ```
     */
    async sendSubmissionStatusUpdate(request: SubmissionStatusUpdateRequest): Promise<ApiResponse<void>> {
        try {
            await this.post('/notifications/admin/submission-status', request, {
                cache: false // Don't cache write operations
            });
            return { success: true };
        }
        catch (error) {
            logger.error('Error sending submission status update:', error, {});
            return {
                success: false,
                error: error instanceof Error ? error.message : 'Failed to send status update notification'
            };
        }
    }
    /**
     * Send approval request notification
     *
     * @param request - Approval request notification data
     * @returns Promise resolving to operation result
     *
     * @example
     * ```typescript
     * const result = await notificationService.sendApprovalRequest({
     *   submissionId: 123,
     *   submissionType: 'venue',
     *   reviewerId: 456,
     *   message: 'Please review this venue submission'
     * });
     * ```
     */
    async sendApprovalRequest(request: ApprovalRequestNotification): Promise<ApiResponse<void>> {
        try {
            await this.post('/notifications/admin/approval-request', request, {
                cache: false // Don't cache write operations
            });
            return { success: true };
        }
        catch (error) {
            logger.api('Error sending approval request:', error);
            return {
                success: false,
                error: error instanceof Error ? error.message : 'Failed to send approval request notification'
            };
        }
    }
    // ========================================
    // NOTIFICATION TEMPLATES
    // ========================================
    /**
     * Get notification templates
     *
     * @param type - Optional template type filter
     * @param activeOnly - Whether to return only active templates
     * @returns Promise resolving to template list
     *
     * @example
     * ```typescript
     * // Get all active templates
     * const templates = await notificationService.getNotificationTemplates();
     *
     * // Get templates of specific type
     * const emailTemplates = await notificationService.getNotificationTemplates('email');
     * ```
     */
    async getNotificationTemplates(type?: string, activeOnly: boolean = true): Promise<ApiResponse<NotificationTemplate[]>> {
        try {
            const params = new URLSearchParams();
            if (type)
                params.append('type', type);
            if (activeOnly)
                params.append('activeOnly', 'true');
            const endpoint = `/notifications/templates?${params.toString()}`;
            const response = await this.get<NotificationTemplate[]>(endpoint, {
                cache: true,
                cacheDuration: 300000 // 5 minute cache for templates
            });
            return {
                success: true,
                data: response
            };
        }
        catch (error) {
            logger.error('Error fetching notification templates:', error, {});
            return {
                success: false,
                error: error instanceof Error ? error.message : 'Failed to fetch notification templates'
            };
        }
    }
    /**
     * Create or update notification template
     *
     * @param template - Template data (without id, created_at, updated_at)
     * @returns Promise resolving to operation result with template ID
     *
     * @example
     * ```typescript
     * const result = await notificationService.upsertNotificationTemplate({
     *   name: 'Welcome Email',
     *   type: 'email',
     *   title: 'Welcome to the platform',
     *   message: 'Hello {{user_name}}, welcome to our platform!',
     *   variables: ['user_name'],
     *   is_active: true
     * });
     * ```
     */
    async upsertNotificationTemplate(template: Omit<NotificationTemplate, 'id' | 'created_at' | 'updated_at'>): Promise<ApiResponse<{
        id: number;
    }>> {
        try {
            const response = await this.post<{
                id: number;
            }>('/notifications/templates', template, {
                cache: false // Don't cache write operations
            });
            // Invalidate template cache
            this.invalidateTemplateCache();
            return {
                success: true,
                data: response
            };
        }
        catch (error) {
            logger.error('Error upserting notification template:', error, {});
            return {
                success: false,
                error: error instanceof Error ? error.message : 'Failed to upsert notification template'
            };
        }
    }
    /**
     * Delete notification template
     *
     * @param templateId - ID of the template to delete
     * @returns Promise resolving to operation result
     *
     * @example
     * ```typescript
     * const result = await notificationService.deleteNotificationTemplate(123);
     * if (result.success) {
     *   logger.debug('Template deleted successfully');
     * }
     * ```
     */
    async deleteNotificationTemplate(templateId: number): Promise<ApiResponse<void>> {
        try {
            await this.delete(`/notifications/templates/${templateId}`, {
                cache: false // Don't cache write operations
            });
            // Invalidate template cache
            this.invalidateTemplateCache();
            return { success: true };
        }
        catch (error) {
            logger.error('Error deleting notification template:', error, {});
            return {
                success: false,
                error: error instanceof Error ? error.message : 'Failed to delete notification template'
            };
        }
    }
    // ========================================
    // USER PREFERENCES
    // ========================================
    /**
     * Get user notification preferences
     *
     * @returns Promise resolving to user preferences or null if not found
     *
     * @example
     * ```typescript
     * const preferences = await notificationService.getUserNotificationPreferences();
     * if (preferences) {
     *   logger.debug('Email notifications:', preferences.email_notifications);
     * }
     * ```
     */
    async getUserNotificationPreferences(): Promise<UserNotificationPreferences | null> {
        try {
            const response = await this.get<UserNotificationPreferences>('/notifications/preferences', {
                cache: true,
                cacheDuration: 300000 // 5 minute cache for preferences
            });
            return response;
        }
        catch (error) {
            logger.error('Error fetching notification preferences:', error, {});
            return null;
        }
    }
    /**
     * Update user notification preferences
     *
     * @param preferences - Partial preferences to update
     * @returns Promise resolving to operation result
     *
     * @example
     * ```typescript
     * const result = await notificationService.updateUserNotificationPreferences({
     *   email_notifications: true,
     *   push_notifications: false,
     *   quiet_hours_start: '22:00',
     *   quiet_hours_end: '08:00'
     * });
     * ```
     */
    async updateUserNotificationPreferences(preferences: Partial<UserNotificationPreferences>): Promise<ApiResponse<void>> {
        try {
            await this.put('/notifications/preferences', preferences, {
                cache: false // Don't cache write operations
            });
            // Invalidate preferences cache
            this.invalidatePreferencesCache();
            return { success: true };
        }
        catch (error) {
            logger.error('Error updating notification preferences:', error, {});
            return {
                success: false,
                error: error instanceof Error ? error.message : 'Failed to update notification preferences'
            };
        }
    }
    // ========================================
    // REAL-TIME NOTIFICATIONS
    // ========================================
    /**
     * Subscribe to real-time notifications
     *
     * @param callback - Function to call when new notifications arrive
     * @returns Unsubscribe function
     *
     * @example
     * ```typescript
     * const unsubscribe = notificationService.subscribeToNotifications((notification) => {
     *   logger.debug('New notification:', notification.title);
     *   // Update UI, show toast, etc.
     * });
     *
     * // Later, unsubscribe
     * unsubscribe();
     * ```
     */
    subscribeToNotifications(callback: (notification: Notification) => void): () => void {
        // This would typically use WebSockets or Server-Sent Events
        // For now, we'll implement polling as a fallback
        const interval = setInterval(async () => {
            try {
                const result = await this.getNotificationCount();
                if (result.success && result.data && result.data.unread > 0) {
                    // Fetch latest notifications and call callback
                    const notificationsResult = await this.getUserNotifications({
                        page: 1,
                        limit: 5,
                        isRead: false
                    });
                    if (notificationsResult.success && notificationsResult.data) {
                        notificationsResult.data.notifications.forEach(notification => {
                            callback(notification);
                        });
                    }
                }
            }
            catch (error) {
                logger.error('Error in notification polling:', error, {});
            }
        }, 30000); // Poll every 30 seconds
        // Return unsubscribe function
        return () => clearInterval(interval);
    }
    /**
     * Mark notification as read in real-time
     *
     * @param notificationId - ID of the notification to mark as read
     * @returns Promise resolving when complete
     *
     * @example
     * ```typescript
     * await notificationService.markAsReadRealTime(123);
     * // Notification is marked as read and cache is updated
     * ```
     */
    async markAsReadRealTime(notificationId: number): Promise<void> {
        await this.markAsRead(notificationId);
        // Emit real-time update to other connected clients if using WebSockets
    }
    // ========================================
    // CACHE MANAGEMENT
    // ========================================
    /**
     * Invalidate notification-related cache entries
     */
    private invalidateNotificationCache(): void {
        const keysToDelete: string[] = [];
        for (const key of this.requestCache.keys()) {
            if (key.includes('/dashboard/notifications') || key.includes('/notifications/count') || key.includes('/notifications/unified') || (key.includes('/notifications') && !key.includes('/notifications/admin') && !key.includes('/notifications/templates') && !key.includes('/notifications/preferences'))) {
                keysToDelete.push(key);
            }
        }
        keysToDelete.forEach(key => this.requestCache.delete(key));
    }
    /**
     * Invalidate template-related cache entries
     */
    private invalidateTemplateCache(): void {
        const keysToDelete: string[] = [];
        for (const key of this.requestCache.keys()) {
            if (key.includes('/notifications/templates')) {
                keysToDelete.push(key);
            }
        }
        keysToDelete.forEach(key => this.requestCache.delete(key));
    }
    /**
     * Invalidate preferences-related cache entries
     */
    private invalidatePreferencesCache(): void {
        const keysToDelete: string[] = [];
        for (const key of this.requestCache.keys()) {
            if (key.includes('/notifications/preferences')) {
                keysToDelete.push(key);
            }
        }
        keysToDelete.forEach(key => this.requestCache.delete(key));
    }
    /**
     * Clear all cache entries
     *
     * @example
     * ```typescript
     * notificationService.clearCache();
     * ```
     */
    public clearCache(): void {
        this.requestCache.clear();
        this.pendingRequests.clear();
    }
    /**
     * Get cache statistics
     *
     * @returns Cache statistics object
     *
     * @example
     * ```typescript
     * const stats = notificationService.getCacheStats();
     * logger.debug(`Cache size: ${stats.size}, Hit rate: ${stats.hitRate}%`);
     * ```
     */
    public getCacheStats(): {
        size: number;
        maxSize: number;
        pendingRequests: number;
        errorCount: number;
    } {
        return {
            size: this.requestCache.size,
            maxSize: this.config.maxCacheSize,
            pendingRequests: this.pendingRequests.size,
            errorCount: this.errorLog.length
        };
    }
    /**
     * Get error log for debugging
     *
     * @returns Array of recent errors
     *
     * @example
     * ```typescript
     * const errors = notificationService.getErrorLog();
     * logger.debug('Recent errors:', errors);
     * ```
     */
    public getErrorLog(): NotificationServiceError[] {
        return [...this.errorLog];
    }
}
export default new NotificationService();
